import {useAuth} from '@/components/AuthProvider';
import {useAnalytics} from '@/hooks/useAnalytics';
import {DEFAULT_CUBIC, DEFAULT_DURATION} from '@/styles/animation';
import {Copy, Download, Instagram, Link, MoreHorizontal} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Sheet} from 'react-modal-sheet';

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventTitle: string;
}

type ShareAction =
  | 'instagram_story'
  | 'copy_to_clipboard'
  | 'save'
  | 'copy_link'
  | 'more';

type FeedbackState = {
  message: string;
  type: 'success' | 'error';
} | null;

/**
 * Builds a shareable URL by appending UTM attribution params
 * to the current page URL.
 */
function buildShareUrl(userId: string | undefined): string {
  const url = new URL(window.location.href);
  url.searchParams.set('utm_source', 'share');
  if (userId) {
    url.searchParams.set('shared_by', userId);
  }
  return url.toString();
}

/**
 * Fetches the share image for an event as a Blob.
 */
async function fetchShareImageBlob(
  eventId: string,
  signal?: AbortSignal
): Promise<Blob> {
  const response = await fetch(`/api/share-image/${eventId}`, {signal});
  if (!response.ok) {
    throw new Error(`Failed to fetch share image: ${response.status}`);
  }
  return response.blob();
}

/**
 * Mobile bottom sheet for sharing an event. Displays an image preview
 * fetched from the share image API and provides 5 action buttons:
 * Instagram Story, Copy to Clipboard, Save, Copy Link, and More.
 * Each action fires a Mixpanel tracking event.
 */
export function ShareSheet({
  isOpen,
  onClose,
  eventId,
  eventTitle,
}: ShareSheetProps) {
  const {track} = useAnalytics();
  const {profile} = useAuth();
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const imageUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();

    setIsLoading(true);
    fetchShareImageBlob(eventId, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        const url = URL.createObjectURL(blob);
        imageUrlRef.current = url;
        setImageBlob(blob);
        setImageUrl(url);
      })
      .catch(() => {
        // Image failed to load or request was aborted
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = null;
      }
      setImageBlob(null);
      setImageUrl(null);
    };
  }, [isOpen, eventId]);

  const showFeedback = useCallback(
    (message: string, type: 'success' | 'error') => {
      setFeedback({message, type});
      setTimeout(() => setFeedback(null), 2000);
    },
    []
  );

  const trackAction = useCallback(
    (action: ShareAction) => {
      track('Share Clicked', {
        action,
        platform: 'mobile',
        content_type: 'event',
        content_id: eventId,
        shared_by: profile?.id,
      });
    },
    [track, eventId, profile?.id]
  );

  const handleInstagramStory = useCallback(async () => {
    trackAction('instagram_story');
    if (!imageBlob) {
      showFeedback('Image still loading', 'error');
      return;
    }
    const file = new File([imageBlob], 'share.png', {type: 'image/png'});
    const shareData = {files: [file]};
    if (!navigator.canShare?.(shareData)) {
      showFeedback('File sharing not supported', 'error');
      return;
    }
    try {
      await navigator.share(shareData);
    } catch {
      // User cancelled
    }
  }, [trackAction, imageBlob, showFeedback]);

  const handleCopyToClipboard = useCallback(async () => {
    trackAction('copy_to_clipboard');
    try {
      if (imageBlob) {
        await navigator.clipboard.write([
          new ClipboardItem({'image/png': imageBlob}),
        ]);
        showFeedback('Image copied', 'success');
      }
    } catch {
      showFeedback('Failed to copy image', 'error');
    }
  }, [trackAction, imageBlob, showFeedback]);

  const handleSave = useCallback(() => {
    trackAction('save');
    if (imageUrl) {
      const anchor = document.createElement('a');
      anchor.href = imageUrl;
      anchor.download = `${eventTitle.replace(/\s+/g, '-').toLowerCase()}-share.png`;
      anchor.click();
      showFeedback('Image saved', 'success');
    }
  }, [trackAction, imageUrl, eventTitle, showFeedback]);

  const handleCopyLink = useCallback(async () => {
    trackAction('copy_link');
    const url = buildShareUrl(profile?.id);
    try {
      await navigator.clipboard.writeText(url);
      showFeedback('Link copied', 'success');
    } catch {
      showFeedback('Failed to copy link', 'error');
    }
  }, [trackAction, profile?.id, showFeedback]);

  const handleMore = useCallback(async () => {
    trackAction('more');
    const url = buildShareUrl(profile?.id);
    if (!navigator.share) {
      showFeedback('Sharing not supported', 'error');
      return;
    }
    try {
      await navigator.share({title: eventTitle, url});
    } catch {
      // User cancelled
    }
  }, [trackAction, profile?.id, eventTitle, showFeedback]);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container>
        <Sheet.Header />
        <Sheet.Content>
          <div className="px-6 pb-6 flex flex-col gap-5">
            {/* Image preview — max-height keeps buttons visible on small screens */}
            <div className="w-full flex justify-center">
              {isLoading && (
                <div
                  className="w-full aspect-[9/16] max-h-[60dvh] animate-pulse bg-gray-200 rounded-xl"
                  aria-label="Loading share image"
                  role="status"
                />
              )}
              {!isLoading && imageUrl && (
                <img
                  src={imageUrl}
                  alt={`Share image for ${eventTitle}`}
                  className="max-h-[60dvh] rounded-xl"
                />
              )}
            </div>

            {/* Action buttons / feedback indicator */}
            <div className="relative min-h-[72px]">
              <AnimatePresence mode="wait">
                {feedback ? (
                  <FeedbackIndicator
                    key="feedback"
                    message={feedback.message}
                    type={feedback.type}
                  />
                ) : (
                  <motion.div
                    key="buttons"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.2}}
                    className="grid grid-cols-5 gap-2"
                  >
                    <ActionButton
                      icon={<Instagram className="size-5" />}
                      label="Story"
                      onClick={handleInstagramStory}
                    />
                    <ActionButton
                      icon={<Copy className="size-5" />}
                      label="Copy"
                      onClick={handleCopyToClipboard}
                    />
                    <ActionButton
                      icon={<Download className="size-5" />}
                      label="Save"
                      onClick={handleSave}
                    />
                    <ActionButton
                      icon={<Link className="size-5" />}
                      label="Link"
                      onClick={handleCopyLink}
                    />
                    <ActionButton
                      icon={<MoreHorizontal className="size-5" />}
                      label="More"
                      onClick={handleMore}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onTap={onClose} />
    </Sheet>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

/**
 * iOS-style circular action button with neutral background
 * and label underneath.
 */
function ActionButton({icon, label, onClick}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5"
    >
      <div className="size-12 rounded-full flex items-center justify-center text-neutral-700 bg-neutral-100 active:bg-neutral-200 transition-colors">
        {icon}
      </div>
      <span className="text-[11px] text-gray-600 leading-tight text-center">
        {label}
      </span>
    </button>
  );
}

/**
 * Animated feedback indicator that draws a checkmark (or X),
 * expands to reveal the message, then fades out.
 * Uses motion for orchestrated animations.
 */
function FeedbackIndicator({
  message,
  type,
}: {
  message: string;
  type: 'success' | 'error';
}) {
  const isSuccess = type === 'success';
  const color = isSuccess ? '#16a34a' : '#ef4444';

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center"
      role="status"
      initial={{opacity: 0, scale: 0.8}}
      animate={{opacity: 1, scale: 1}}
      exit={{opacity: 0}}
      transition={{duration: 0.2, ease: DEFAULT_CUBIC}}
    >
      <div className="flex items-center rounded-full bg-white px-3 py-2 shadow-sm border border-neutral-100">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="shrink-0"
        >
          {isSuccess ? (
            <motion.path
              d="M4 10.5L8 14.5L16 6.5"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{pathLength: 0}}
              animate={{pathLength: 1}}
              transition={{
                duration: DEFAULT_DURATION,
                ease: DEFAULT_CUBIC,
                delay: 0,
              }}
            />
          ) : (
            <>
              <motion.path
                d="M5 5L15 15"
                stroke={color}
                strokeWidth="2.5"
                strokeLinecap="round"
                initial={{pathLength: 0}}
                animate={{pathLength: 1}}
                transition={{duration: 0.3, ease: 'easeOut', delay: 0.2}}
              />
              <motion.path
                d="M15 5L5 15"
                stroke={color}
                strokeWidth="2.5"
                strokeLinecap="round"
                initial={{pathLength: 0}}
                animate={{pathLength: 1}}
                transition={{duration: 0.3, ease: 'easeOut', delay: 0.35}}
              />
            </>
          )}
        </svg>
        <motion.span
          className="text-sm font-medium whitespace-nowrap overflow-hidden inline-block"
          style={{color}}
          initial={{maxWidth: 0, marginLeft: 0, opacity: 0}}
          animate={{maxWidth: 200, marginLeft: 8, opacity: 1}}
          transition={{
            duration: DEFAULT_DURATION,
            ease: DEFAULT_CUBIC,
            delay: 0.5,
          }}
        >
          {message}
        </motion.span>
      </div>
    </motion.div>
  );
}
